import { Table, type TableProps } from 'antd';
import { pick } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { type RoleListDataFragment } from '../../../api/fragments/RoleListData';
import { useListRolesQuery } from '../../../api/queries/listRoles';
import { RoleSortingField } from '../../../api/types';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import PaginatedTableWithContext from '../../../components/PaginatedTableWithContext';
import InternalErrorResult from '../../../components/results/InternalErrorResult';
import useSearchInDropDown from '../../../components/useSearchInDropDown';
import makeGetSortingRule from '../../../utilities/makeGetSortingRule';
import renderSearchIcon from '../../../utilities/renderSearchIcon';
import useSortAndFilterCache, { SortAndFilterCacheKey } from '../../../utilities/useSortAndFilterCache';
import useListReducer from './useListReducer';

const getSortField = makeGetSortingRule((field): RoleSortingField => {
    switch (field) {
        case 'displayName':
            return RoleSortingField.DisplayName;

        case 'userCount':
            return RoleSortingField.UserCount;

        case 'description':
            return RoleSortingField.Description;

        default:
            throw new Error('Unknown Sorting Field!');
    }
});

const RoleList = () => {
    const { t } = useTranslation('roleList');

    // get company context
    const company = useCompany(true);

    const [currentCache, setCache] = useSortAndFilterCache(SortAndFilterCacheKey.Role);
    // get state from a reducer
    const [state, dispatch] = useListReducer(currentCache);

    // fetch data
    const { page, pageSize, sort, filter } = state;
    const { loading, error, data } = useListRolesQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: {
                ...state.filter,
                companyId: company?.id,
                displayName: state.filter.displayName || null,
                description: state.filter.description || null,
            },
            sort: pick(['field', 'order'], sort),
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
        },
    });

    // prepare items as a data source
    const dataSource = useMemo(() => (data?.page?.items || []).map(item => ({ ...item, key: item.id })), [data]);
    const total = data?.page?.count || 0;

    const searchBox = useSearchInDropDown();

    const navigate = useNavigate();

    const onChange = useCallback<TableProps<RoleListDataFragment>['onChange']>(
        (pagination, filters, sorter, extra) => {
            switch (extra.action) {
                case 'sort':
                    return dispatch({ type: 'setSort', sortBy: getSortField(sorter) });

                case 'filter':
                    return dispatch({
                        type: 'setFilter',
                        filterBy: {
                            displayName: !filters.displayName ? null : (filters.displayName[0] as string),
                            description: !filters.description ? null : (filters.description[0] as string),
                        },
                    });

                default:
                    return undefined;
            }
        },
        [dispatch]
    );

    if (!loading) {
        if (error) {
            return <InternalErrorResult />;
        }
    }

    return (
        <PaginatedTableWithContext
            company={company}
            dataSource={dataSource}
            dispatch={dispatch}
            loading={loading}
            onChange={onChange}
            onRow={(row: RoleListDataFragment) => ({
                onClick: () => {
                    setCache(state);
                    navigate(`/admin/accesses/roles/${row.id}`);
                },
                style: { cursor: 'pointer' },
            })}
            rowKey="id"
            state={state}
            tableName={t('roleList:title')}
            total={total}
        >
            {!company && (
                <Table.Column
                    key="company"
                    dataIndex={['company', 'displayName']}
                    title={t('roleList:columns.company')}
                />
            )}
            <Table.Column<RoleListDataFragment>
                key="displayName"
                dataIndex="displayName"
                filterDropdown={searchBox.render('description')}
                filterIcon={renderSearchIcon}
                filteredValue={filter.displayName ? [filter.displayName] : undefined}
                onFilterDropdownOpenChange={searchBox.autoFocus}
                sortOrder={sort.field === RoleSortingField.DisplayName ? sort.orderValue : undefined}
                title={t('roleList:columns.name')}
                sorter
            />
            <Table.Column<RoleListDataFragment>
                key="description"
                dataIndex="description"
                filterDropdown={searchBox.render('description')}
                filterIcon={renderSearchIcon}
                filteredValue={filter.description ? [filter.description] : undefined}
                onFilterDropdownOpenChange={searchBox.autoFocus}
                sortOrder={sort.field === RoleSortingField.Description ? sort.orderValue : undefined}
                title={t('roleList:columns.description')}
                sorter
            />
            <Table.Column
                key="userCount"
                dataIndex="userCount"
                sortOrder={sort.field === RoleSortingField.UserCount ? sort.orderValue : undefined}
                title={t('roleList:columns.users')}
                sorter
            />
        </PaginatedTableWithContext>
    );
};

export default RoleList;
